import type {
  WeeklyCallDay,
  WeeklyCallSchedule,
  WeeklyCallSlot,
} from '@/types/api';

export const WEEKDAY_KEYS = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
] as const;

const DEFAULT_WINDOW: WeeklyCallSlot = { start: '09:00', end: '17:00' };
const LAST_MINUTE = 23 * 60 + 59;

function openDay(day: number): WeeklyCallDay {
  return { day, closed: false, windows: [] };
}

export function isOpenAllDay(day: WeeklyCallDay): boolean {
  return !day.closed && day.windows.length === 0;
}

export function openWeek(timezone: string): WeeklyCallSchedule {
  return { timezone, days: WEEKDAY_KEYS.map((_, day) => openDay(day)) };
}

export function isOpenWeek(schedule: WeeklyCallSchedule): boolean {
  return schedule.days.every(isOpenAllDay);
}

function withDay(
  schedule: WeeklyCallSchedule,
  next: WeeklyCallDay
): WeeklyCallSchedule {
  return {
    ...schedule,
    days: schedule.days.map(day => (day.day === next.day ? next : day)),
  };
}

export function setDayClosed(
  schedule: WeeklyCallSchedule,
  dayIndex: number,
  closed: boolean
): WeeklyCallSchedule {
  return withDay(schedule, {
    day: dayIndex,
    closed,
    windows: closed ? [] : [{ ...DEFAULT_WINDOW }],
  });
}

function clockFromMinutes(total: number): string {
  const hour = Math.floor(total / 60);
  const minute = total % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function nextWindow(windows: WeeklyCallSlot[]): WeeklyCallSlot {
  const latestEnd = windows.reduce(
    (latest, window) => Math.max(latest, minutesFromClock(window.end)),
    -1
  );
  if (latestEnd < 0 || latestEnd >= LAST_MINUTE) {
    return { ...DEFAULT_WINDOW };
  }
  return {
    start: clockFromMinutes(latestEnd),
    end: clockFromMinutes(Math.min(latestEnd + 60, LAST_MINUTE)),
  };
}

export function addDayWindow(
  schedule: WeeklyCallSchedule,
  dayIndex: number
): WeeklyCallSchedule {
  const current = schedule.days[dayIndex];
  return withDay(schedule, {
    ...current,
    closed: false,
    windows: [...current.windows, nextWindow(current.windows)],
  });
}

export function updateDayWindow(
  schedule: WeeklyCallSchedule,
  dayIndex: number,
  windowIndex: number,
  next: WeeklyCallSlot
): WeeklyCallSchedule {
  const current = schedule.days[dayIndex];
  return withDay(schedule, {
    ...current,
    windows: current.windows.map((window, index) =>
      index === windowIndex ? next : window
    ),
  });
}

export function removeDayWindow(
  schedule: WeeklyCallSchedule,
  dayIndex: number,
  windowIndex: number
): WeeklyCallSchedule {
  const current = schedule.days[dayIndex];
  const windows = current.windows.filter((_, index) => index !== windowIndex);
  return withDay(
    schedule,
    windows.length === 0 ? openDay(dayIndex) : { ...current, windows }
  );
}

export function copyDayTo(
  schedule: WeeklyCallSchedule,
  sourceIndex: number,
  targetIndexes: number[]
): WeeklyCallSchedule {
  const source = schedule.days[sourceIndex];
  return {
    ...schedule,
    days: schedule.days.map(day =>
      targetIndexes.includes(day.day)
        ? {
            ...source,
            day: day.day,
            windows: source.windows.map(window => ({ ...window })),
          }
        : day
    ),
  };
}

export function minutesFromClock(value: string): number {
  const [hour, minute] = value.split(':');
  return Number(hour) * 60 + Number(minute);
}

function slotCoveredByWindows(
  slot: WeeklyCallSlot,
  windows: WeeklyCallSlot[]
): boolean {
  let cursor = minutesFromClock(slot.start);
  const end = minutesFromClock(slot.end);
  const sorted = [...windows].sort(
    (left, right) => minutesFromClock(left.start) - minutesFromClock(right.start)
  );
  for (const window of sorted) {
    const start = minutesFromClock(window.start);
    const windowEnd = minutesFromClock(window.end);
    if (windowEnd <= cursor) {
      continue;
    }
    if (start > cursor) {
      return false;
    }
    cursor = Math.max(cursor, windowEnd);
    if (cursor >= end) {
      return true;
    }
  }
  return cursor >= end;
}

export function windowOutsideEstablishmentHours(
  slot: WeeklyCallSlot,
  dayIndex: number,
  establishment: WeeklyCallSchedule
): boolean {
  if (minutesFromClock(slot.start) >= minutesFromClock(slot.end)) {
    return false;
  }
  const establishmentDay = establishment.days.find(day => day.day === dayIndex);
  if (!establishmentDay || isOpenAllDay(establishmentDay)) {
    return false;
  }
  if (establishmentDay.closed) {
    return true;
  }
  return !slotCoveredByWindows(slot, establishmentDay.windows);
}

export function dayHasHoursOutsideEstablishment(
  day: WeeklyCallDay,
  establishment: WeeklyCallSchedule
): boolean {
  if (day.closed || isOpenAllDay(day)) {
    return false;
  }
  return day.windows.some(window =>
    windowOutsideEstablishmentHours(window, day.day, establishment)
  );
}

export function dayWindowError(day: WeeklyCallDay): 'order' | 'overlap' | null {
  const ordered = [...day.windows].sort(
    (left, right) =>
      minutesFromClock(left.start) - minutesFromClock(right.start)
  );
  let previousEnd: number | null = null;
  for (const window of ordered) {
    const start = minutesFromClock(window.start);
    const end = minutesFromClock(window.end);
    if (start >= end) {
      return 'order';
    }
    if (previousEnd !== null && start < previousEnd) {
      return 'overlap';
    }
    previousEnd = end;
  }
  return null;
}

export function schedulesEqual(
  left: WeeklyCallSchedule,
  right: WeeklyCallSchedule
): boolean {
  return left.days.every((day, index) => {
    const other = right.days[index];
    return (
      day.closed === other.closed &&
      day.windows.length === other.windows.length &&
      day.windows.every(
        (window, windowIndex) =>
          window.start === other.windows[windowIndex].start &&
          window.end === other.windows[windowIndex].end
      )
    );
  });
}

export function scheduleHasErrors(schedule: WeeklyCallSchedule): boolean {
  return schedule.days.some(day => dayWindowError(day) !== null);
}

export function daySummary(
  day: WeeklyCallDay,
  openLabel: string,
  closedLabel: string
): string {
  if (day.closed) {
    return closedLabel;
  }
  if (isOpenAllDay(day)) {
    return openLabel;
  }
  return day.windows.map(window => `${window.start}–${window.end}`).join(', ');
}

export function groupedDaySummaries(
  schedule: WeeklyCallSchedule,
  labels: string[],
  openLabel: string,
  closedLabel: string,
  daySummaryFn: (
    day: WeeklyCallDay,
    open: string,
    closed: string
  ) => string = (day, open, closed) => daySummary(day, open, closed)
): { label: string; summary: string }[] {
  const groups: { label: string; summary: string }[] = [];
  schedule.days.forEach(day => {
    const summary = daySummaryFn(day, openLabel, closedLabel);
    const previous = groups[groups.length - 1];
    if (previous && previous.summary === summary) {
      const [start] = previous.label.split('–');
      previous.label = `${start}–${labels[day.day]}`;
      return;
    }
    groups.push({ label: labels[day.day], summary });
  });
  return groups;
}
